import { Directive, EventEmitter, Input, OnChanges, OnInit, Output } from "@angular/core";
import { ControlValueAccessor } from "@angular/forms";

@Directive()
export abstract class BaseFormFieldComponent<T> implements OnInit, OnChanges, ControlValueAccessor {
  @Input() public icon: string | undefined;
  @Input() public label: string | undefined;
  @Input() public placeholder: string | undefined;
  @Input() public size: "small" | "medium" | "large" = "small";
  @Input() public initialValue: T;
  @Input() public readOnly = false;

  @Output() public onValueChange = new EventEmitter<T>();

  public baseCssClass: string;
  public disabled = false;
  public readOnlyValue: string;
  public touched = false;

  public fieldValue: T;

  public onChange = (_value: T) => {};
  public onTouched = () => {};

  protected constructor() {}

  public ngOnInit() {
    switch (this.size) {
      case "small":
        this.baseCssClass = "small-label";
        break;
      case "medium":
        this.baseCssClass = "medium-label";
        break;
      case "large":
        this.baseCssClass = "large-label";
        break;
    }
  }

  public async ngOnChanges(): Promise<void> {
    this.disabled = this.readOnly || this.disabled;

    if (!this.initialValue || !!this.fieldValue) {
      return;
    }

    this.readOnlyValue = await this.toDisplayValue(this.initialValue);
    this.fieldValue = this.initialValue;

    await this.writeValue(this.fieldValue);
  }

  protected async onInputChangeInternal(value: T): Promise<void> {
    this.fieldValue = value;
    this.readOnlyValue = await this.toDisplayValue(value);

    this.onChange(this.fieldValue);
    this.markAsTouched();
    this.onValueChange.emit(this.fieldValue);
  }

  public async writeValue(value: T): Promise<void> {
    this.readOnlyValue = await this.toDisplayValue(value);
    this.fieldValue = value;
  }

  public registerOnChange(onChange: any): void {
    this.onChange = onChange;
  }

  public registerOnTouched(onTouched: any): void {
    this.onTouched = onTouched;
  }

  public markAsTouched(): void {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  public setDisabledState(disabled: boolean): void {
    this.disabled = disabled || this.readOnly;
  }

  public isMediumOrLarge(): boolean {
    return this.size === "medium" || this.size === "large";
  }

  public getPlaceholderValue(): string {
    if (this.readOnlyValue) {
      return this.readOnlyValue;
    }

    if (this.placeholder) {
      return this.placeholder;
    }

    const labelOrDefault = this.label?.toLowerCase() ?? "value";
    if (this.disabled) {
      return `No ${labelOrDefault}`;
    }

    return `Enter ${labelOrDefault}`;
  }

  protected abstract toDisplayValue(input: T): string | Promise<string>;
}
